import { Link } from 'react-router-dom';
import { useCountUp } from '../../../../hooks/useCountUp';
import AbattoirPhoto from '../../assets/img/ProcessingAbattoir.jpg';
import LeatherPhoto from '../../assets/img/ProcessingLeather.jpg';
import DairyPhoto from '../../assets/img/ProcessingDairy.jpg';
import FeedPhoto from '../../assets/img/ProcessingFeed.jpg';
import ArrowRight from '../../assets/svg/ArrowRight.svg';
import HoverZoomImage from '../motion/HoverZoomImage';
import { PROCESSING_CATEGORIES, PROCESSING_OVERVIEW_STATS } from '../../data/facilitiesData';

// Each card's number is the row count of the Processing Facilities table its
// "View All" opens, so the two always agree
const STATS = PROCESSING_OVERVIEW_STATS.map((stat) => ({
  ...stat,
  count: PROCESSING_CATEGORIES.find((c) => c.key === stat.key)?.rows.length ?? 0,
}));

// Checkerboard from Figma: each category's photo sits beside its card.
// `stat` indexes into STATS (Abattoir, Hides & Leather, Dairy, Feed).
const TILES = [
  { type: 'photo', src: AbattoirPhoto }, { type: 'card', stat: 0, bg: 'bg-[#d9fbd4]' },
  { type: 'photo', src: LeatherPhoto },  { type: 'card', stat: 1, bg: 'bg-[#d9fbd4]' },
  { type: 'card', stat: 2, bg: 'bg-[rgba(217,251,212,0.5)]' }, { type: 'photo', src: DairyPhoto },
  { type: 'card', stat: 3, bg: 'bg-[rgba(217,251,212,0.5)]' }, { type: 'photo', src: FeedPhoto },
];

const TILE_HEIGHT = 'h-[280px] sm:h-[320px] lg:h-[350px]';

function StatCard({ stat, bg }) {
  const { count, ref } = useCountUp(stat.count);
  const [singular, plural] = stat.noun;

  return (
    <div ref={ref} className={`flex ${TILE_HEIGHT} flex-col justify-between overflow-hidden rounded-[24px] px-4 py-4 sm:px-[27px] sm:py-6 ${bg}`}>
      <div className="flex flex-col gap-3 text-black">
        {/* Figma: "We have over 842" then the facility type on its own line */}
        <p className="font-semibold leading-tight">
          <span className="text-[16px] sm:text-[20px]">We have</span>
          <span className="text-[24px] font-bold text-[#135809] sm:text-[32px]"> {count.toLocaleString('en-NG')}</span>
          <span className="block text-[16px] sm:text-[20px]">{stat.count === 1 ? singular : plural}</span>
        </p>
        <p className="max-w-[238px] text-[12px] font-medium leading-normal sm:text-[13px]">{stat.body}</p>
      </div>

      <Link
        to="facilities-services"
        state={{ topTab: 'processing', category: stat.key }}
        className="group flex items-center justify-between"
      >
        <span className="text-[12px] font-bold text-[#0f3a26]">View All</span>
        <span className="flex items-center justify-center rounded-[20px] bg-[#1fc16b] px-2 py-[5px] transition group-hover:bg-[#19a95c]">
          <img src={ArrowRight} alt="" width="14" height="14" className="h-[14px] w-[14px]" />
        </span>
      </Link>
    </div>
  );
}

export default function ProcessingFacilitiesSection() {
  return (
    <section className="bg-white py-10 sm:py-14 lg:py-16">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 sm:px-8 lg:px-10 pub-xl:px-[100px]">
        <div className="flex flex-col gap-2">
          <h2 className="font-quattrocento text-2xl font-bold text-[#0f3a26] sm:text-3xl lg:text-[32px]">
            PROCESSING FACILITIES OVERVIEW
          </h2>
          <p className="text-sm text-[#0f3a26] sm:text-base lg:text-[18px]">
            Explore how the numbers of processing facilities across different categories in Nigeria.
          </p>
        </div>

        {/* 2 rows of 4 on desktop; the checkerboard still alternates at 2 columns */}
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
          {TILES.map((tile, i) =>
            tile.type === 'photo' ? (
              <HoverZoomImage key={i} src={tile.src} className={`w-full ${TILE_HEIGHT} rounded-[20px]`} />
            ) : (
              <StatCard key={i} stat={STATS[tile.stat]} bg={tile.bg} />
            ),
          )}
        </div>
      </div>
    </section>
  );
}
